/* RESPONSIBLE TEAM: team-conversational-knowledge */
import Model, { attr } from '@ember-data/model';
import { type State } from 'embercom/models/data/matching-system/matching-constants';

export enum TaskSuggestionStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  Expired = 3,
}

export type SuggestionMetadata = {
  topic_name_list?: string[];
  subtopics?: string[];
  action_params?: Record<string, any>;
  mock_response?: Record<string, any>;
  example_queries?: string[];
  conversation_ids?: string[];
  sampled_conversation_ids?: string[];
  guidance_id?: string;
  guidance_state?: number;
  [key: string]: any; // Allow additional metadata fields
};

export enum SuggestionType {
  Action = 'action',
}

export default class TaskSuggestion extends Model {
  @attr('string') declare appId: string;
  @attr('number') declare createdById: number | null;
  @attr('number') declare reviewerId: number | null;
  @attr('boolean') declare success: boolean;
  @attr('string') declare failureReason: string | null;
  @attr('string') declare suggestionType: SuggestionType;
  @attr('number') declare status: TaskSuggestionStatus;
  @attr('date') declare statusUpdatedAt: Date | null;

  // Action-related fields
  @attr('string') declare actionName: string;
  @attr('string') declare actionDescription: string;
  @attr('string') declare actionSummary: string;
  @attr('string') declare actionPath: string;
  @attr('string') declare actionMethod: string;

  // Guidance-related fields
  @attr('string') declare guidanceDescription: string;
  @attr('string') declare guidanceRawDescription: string;
  @attr('string') declare guidanceCategory: string | null;

  // Analytics and metrics
  @attr('number') declare conversationCount: number;
  @attr('string') declare dateRange: string;
  @attr('string') declare confidenceSummary: string;
  @attr('number') declare implementationDifficultyScore: number | null;
  @attr('string') declare implementationDifficultyReasoning: string | null;

  // Metadata stored as JSON
  @attr() declare suggestionMetadata: SuggestionMetadata;

  // Timestamps
  @attr('date') declare createdAt: Date;
  @attr('date') declare updatedAt: Date;

  // Status helper methods
  get isPending(): boolean {
    return this.status === TaskSuggestionStatus.Pending;
  }

  get isApproved(): boolean {
    return this.status === TaskSuggestionStatus.Approved;
  }

  get isRejected(): boolean {
    return this.status === TaskSuggestionStatus.Rejected;
  }

  get isExpired(): boolean {
    return this.status === TaskSuggestionStatus.Expired;
  }

  // Metadata accessors (convenience getters for common fields)
  get topicNameList(): string[] {
    return this.suggestionMetadata?.topic_name_list || [];
  }

  get subtopics(): string[] {
    return this.suggestionMetadata?.subtopics || [];
  }

  get actionParams(): Record<string, any> {
    return this.suggestionMetadata?.action_params || {};
  }

  get mockResponse(): Record<string, any> {
    return this.suggestionMetadata?.mock_response || {};
  }

  get exampleQueries(): string[] {
    return this.suggestionMetadata?.example_queries || [];
  }

  get conversationIds(): string[] {
    return this.suggestionMetadata?.conversation_ids || [];
  }

  get sampledConversationIds(): string[] {
    return this.suggestionMetadata?.sampled_conversation_ids || [];
  }

  get guidanceId(): string | null {
    return this.suggestionMetadata?.guidance_id || null;
  }

  get guidanceState(): State | null {
    return this.suggestionMetadata?.guidance_state || null;
  }

  get isDataGap(): boolean {
    return this.actionMethod?.toUpperCase() === 'GET';
  }

  get isActionGap(): boolean {
    return !this.isDataGap;
  }
}
